import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import PageHero from '../../components/common/PageHero';
import FileUpload from '../../components/common/FileUpload';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';
import {
  BookOpen,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  ArrowLeft,
  Search,
  Tag,
  Eye,
  EyeOff,
  Video,
  FileText,
  Award,
  ExternalLink
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../../components/ui/dialog';

const EducationManagement = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [activeTab, setActiveTab] = useState('resources');
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Resource dialog state
  const [resourceDialogOpen, setResourceDialogOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [resourceForm, setResourceForm] = useState({
    title: '',
    description: '',
    category: '',
    content: '',
    video_url: '',
    resource_url: '',
    file_url: '',
    file_type: '',
    author: '',
    tags: '',
    price: 0,
    is_published: true
  });

  // Category dialog state
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    slug: '',
    icon: 'BookOpen',
    description: ''
  });

  useEffect(() => {
    fetchResources();
    fetchCategories();
  }, []);

  const fetchResources = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${backendUrl}/api/education`);
      setResources(response.data.resources || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load education resources', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/admin/education/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // --- Resource CRUD ---

  const resetResourceForm = () => {
    setResourceForm({
      title: '',
      description: '',
      category: '',
      content: '',
      video_url: '',
      resource_url: '',
      file_url: '',
      file_type: '',
      author: '',
      tags: '',
      price: 0,
      is_published: true
    });
    setEditingResource(null);
  };

  const openCreateResource = () => {
    resetResourceForm();
    setResourceDialogOpen(true);
  };

  const openEditResource = (resource) => {
    setEditingResource(resource);
    setResourceForm({
      title: resource.title || '',
      description: resource.description || '',
      category: resource.category || '',
      content: resource.content || '',
      video_url: resource.video_url || '',
      resource_url: resource.resource_url || '',
      file_url: resource.file_url || '',
      file_type: resource.file_type || '',
      author: resource.author || '',
      tags: Array.isArray(resource.tags) ? resource.tags.join(', ') : (resource.tags || ''),
      price: resource.price || 0,
      is_published: resource.is_published !== false
    });
    setResourceDialogOpen(true);
  };

  const handleSaveResource = async () => {
    if (!resourceForm.title.trim()) {
      toast({ title: 'Missing title', description: 'Title is required', variant: 'destructive' });
      return;
    }

    const payload = {
      ...resourceForm,
      tags: resourceForm.tags
        ? resourceForm.tags.split(',').map(t => t.trim()).filter(Boolean)
        : [],
      category: resourceForm.category || 'General'
    };

    try {
      if (editingResource) {
        await axios.put(`${backendUrl}/api/admin/education/${editingResource.id}`, payload);
        toast({ title: 'Resource Updated', description: 'Education resource has been updated' });
      } else {
        await axios.post(`${backendUrl}/api/admin/education`, payload);
        toast({ title: 'Resource Created', description: 'Education resource has been created' });
      }
      setResourceDialogOpen(false);
      resetResourceForm();
      fetchResources();
    } catch (error) {
      toast({
        title: 'Failed',
        description: error.response?.data?.detail || 'Failed to save resource',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm('Are you sure you want to archive this education resource? It can be restored later.')) return;
    try {
      await axios.delete(`${backendUrl}/api/admin/education/${resourceId}`);
      toast({ title: 'Archived', description: 'Education resource archived' });
      fetchResources();
    } catch (error) {
      toast({ title: 'Failed', description: 'Failed to archive resource', variant: 'destructive' });
    }
  };

  // --- Category CRUD ---

  const resetCategoryForm = () => {
    setCategoryForm({ name: '', slug: '', icon: 'BookOpen', description: '' });
    setEditingCategory(null);
  };

  const openCreateCategory = () => {
    resetCategoryForm();
    setCategoryDialogOpen(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name || '',
      slug: category.slug || '',
      icon: category.icon || 'BookOpen',
      description: category.description || ''
    });
    setCategoryDialogOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast({ title: 'Missing name', description: 'Category name is required', variant: 'destructive' });
      return;
    }

    try {
      if (editingCategory) {
        await axios.put(`${backendUrl}/api/admin/education/categories/${editingCategory.id}`, categoryForm);
        toast({ title: 'Category Updated', description: 'Category has been updated' });
      } else {
        await axios.post(`${backendUrl}/api/admin/education/categories`, categoryForm);
        toast({ title: 'Category Created', description: 'Category has been created' });
      }
      setCategoryDialogOpen(false);
      resetCategoryForm();
      fetchCategories();
    } catch (error) {
      toast({
        title: 'Failed',
        description: error.response?.data?.detail || 'Failed to save category',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to archive this category? It can be restored later.')) return;
    try {
      await axios.delete(`${backendUrl}/api/admin/education/categories/${categoryId}`);
      toast({ title: 'Archived', description: 'Category archived' });
      fetchCategories();
    } catch (error) {
      toast({ title: 'Failed', description: 'Failed to archive category', variant: 'destructive' });
    }
  };

  // --- Helpers ---

  const getCategoryIcon = (iconName) => {
    const map = { BookOpen, Video, FileText, Award, Tag };
    return map[iconName] || BookOpen;
  };

  const filteredResources = resources.filter(r => {
    const matchesSearch = !searchQuery ||
      (r.title && r.title.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (r.description && r.description.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = categoryFilter === 'all' || r.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (isNaN(d)) return '';
    return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHero title="EDUCATION MANAGEMENT" />
      <div className="container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/admin')}
          className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-[#2c3e7d] transition-colors"
        >
          <ArrowLeft size={16} /> Back to Admin Dashboard
        </button>

        {/* Tab Switcher */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'resources' ? 'default' : 'outline'}
            onClick={() => setActiveTab('resources')}
            className={activeTab === 'resources' ? 'bg-[#2c3e7d] hover:bg-[#1e2a5a]' : ''}
          >
            <BookOpen size={16} className="mr-2" />
            Resources ({resources.length})
          </Button>
          <Button
            variant={activeTab === 'categories' ? 'default' : 'outline'}
            onClick={() => setActiveTab('categories')}
            className={activeTab === 'categories' ? 'bg-[#2c3e7d] hover:bg-[#1e2a5a]' : ''}
          >
            <Tag size={16} className="mr-2" />
            Categories ({categories.length})
          </Button>
        </div>

        {/* Resources Tab */}
        {activeTab === 'resources' && (
          <div>
            {/* Filters and Add Button */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
              <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <Input
                    placeholder="Search resources..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={openCreateResource} className="bg-[#2c3e7d] hover:bg-[#1e2a5a]">
                <Plus size={16} className="mr-2" />
                Add Resource
              </Button>
            </div>

            {/* Resources List */}
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2c3e7d]"></div>
              </div>
            ) : filteredResources.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <BookOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-600">No education resources found</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredResources.map(resource => (
                  <Card key={resource.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{resource.title}</h3>
                          {resource.category && (
                            <span className="inline-block mt-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                              {resource.category}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button size="sm" variant="ghost" onClick={() => openEditResource(resource)} className="text-[#2c3e7d]">
                            <Edit size={16} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteResource(resource.id)} className="text-red-600">
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                      {resource.description && (
                        <p className="text-sm text-gray-600 line-clamp-2 mb-2">{resource.description}</p>
                      )}
                      <div className="flex items-center gap-3 flex-wrap text-xs text-gray-500">
                        {resource.author && <span>By: {resource.author}</span>}
                        {resource.created_at && <span>{formatDate(resource.created_at)}</span>}
                        {resource.is_published === false ? (
                          <span className="flex items-center gap-1 text-orange-600"><EyeOff size={12} /> Unpublished</span>
                        ) : (
                          <span className="flex items-center gap-1 text-green-600"><Eye size={12} /> Published</span>
                        )}
                        {resource.video_url && (
                          <span className="flex items-center gap-1 text-purple-600"><Video size={12} /> Video</span>
                        )}
                        {resource.resource_url && (
                          <a href={resource.resource_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#2c3e7d]">
                            <ExternalLink size={12} /> Link
                          </a>
                        )}
                        {resource.file_url && (
                          <a href={resource.file_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#2c3e7d]">
                            <FileText size={12} /> File
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Education Categories</h2>
              <Button onClick={openCreateCategory} className="bg-[#2c3e7d] hover:bg-[#1e2a5a]">
                <Plus size={16} className="mr-2" />
                Add Category
              </Button>
            </div>

            {categories.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Tag className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-600">No categories yet. Create one to organize your resources.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {categories.map(cat => {
                  const IconComponent = getCategoryIcon(cat.icon);
                  const resourceCount = resources.filter(r => r.category === cat.name).length;
                  return (
                    <Card key={cat.id} className="hover:shadow-lg transition-shadow">
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#2c3e7d] bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0">
                              <IconComponent size={20} className="text-[#2c3e7d]" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">{cat.name}</h3>
                              <p className="text-xs text-gray-500">/{cat.slug}</p>
                            </div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <Button size="sm" variant="ghost" onClick={() => openEditCategory(cat)} className="text-[#2c3e7d]">
                              <Edit size={16} />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteCategory(cat.id)} className="text-red-600">
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </div>
                        {cat.description && (
                          <p className="text-sm text-gray-600 mb-2">{cat.description}</p>
                        )}
                        <p className="text-xs text-gray-500">{resourceCount} resource{resourceCount !== 1 ? 's' : ''}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Resource Dialog */}
      <Dialog open={resourceDialogOpen} onOpenChange={setResourceDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingResource ? 'Edit Resource' : 'Create Education Resource'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Title *</Label>
              <Input
                value={resourceForm.title}
                onChange={(e) => setResourceForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Resource title..."
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select
                value={resourceForm.category}
                onValueChange={(v) => setResourceForm(prev => ({ ...prev, category: v }))}
              >
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                  <SelectItem value="General">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={resourceForm.description}
                onChange={(e) => setResourceForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Short description..."
                className="min-h-[60px]"
              />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea
                value={resourceForm.content}
                onChange={(e) => setResourceForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Full content of the resource..."
                className="min-h-[120px]"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Video URL</Label>
                <Input
                  value={resourceForm.video_url}
                  onChange={(e) => setResourceForm(prev => ({ ...prev, video_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>Resource URL</Label>
                <Input
                  value={resourceForm.resource_url}
                  onChange={(e) => setResourceForm(prev => ({ ...prev, resource_url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
              <div className="md:col-span-2">
                <FileUpload
                  folder="education"
                  adminOnly={true}
                  label="Upload Document File (PDF, Word, Excel, etc.)"
                  currentUrl={resourceForm.file_url}
                  onUpload={(url, filename) => {
                    const ext = filename ? '.' + filename.split('.').pop().toUpperCase().replace('.', '') : '';
                    setResourceForm(prev => ({ ...prev, file_url: url, file_type: ext }));
                  }}
                  onClear={() => setResourceForm(prev => ({ ...prev, file_url: '', file_type: '' }))}
                />
              </div>
              <div>
                <Label>Price (R) - 0 for free</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={resourceForm.price}
                  onChange={(e) => setResourceForm(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                  placeholder="0.00"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Author</Label>
                <Input
                  value={resourceForm.author}
                  onChange={(e) => setResourceForm(prev => ({ ...prev, author: e.target.value }))}
                  placeholder="Author name..."
                />
              </div>
              <div>
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={resourceForm.tags}
                  onChange={(e) => setResourceForm(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="CPD, chiropractic, sports..."
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_published"
                checked={resourceForm.is_published}
                onChange={(e) => setResourceForm(prev => ({ ...prev, is_published: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300"
              />
              <Label htmlFor="is_published" className="cursor-pointer">Published</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResourceDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveResource} className="bg-[#2c3e7d] hover:bg-[#1e2a5a]">
              <Save size={16} className="mr-2" />
              {editingResource ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Create Category'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. CPD Courses"
              />
            </div>
            <div>
              <Label>Slug</Label>
              <Input
                value={categoryForm.slug}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, slug: e.target.value }))}
                placeholder="cpd-courses (auto-generated if empty)"
              />
            </div>
            <div>
              <Label>Icon</Label>
              <Select
                value={categoryForm.icon}
                onValueChange={(v) => setCategoryForm(prev => ({ ...prev, icon: v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="BookOpen">Book</SelectItem>
                  <SelectItem value="Video">Video</SelectItem>
                  <SelectItem value="FileText">Document</SelectItem>
                  <SelectItem value="Award">Award</SelectItem>
                  <SelectItem value="Tag">Tag</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Category description..."
                className="min-h-[60px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveCategory} className="bg-[#2c3e7d] hover:bg-[#1e2a5a]">
              <Save size={16} className="mr-2" />
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EducationManagement;
